import React, { useState, useEffect } from "react";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from "axios";
import styles from "./DukamoHawassaPage.module.css";

// --- ORIGINAL IMAGE IMPORTS (Fallbacks) ---
import hawassaHeroImage from "../assets/Dukamo Hawassa/Hawassa.jpg";
import slide1 from "../assets/Dukamo Hawassa/slide1.jpg";
import slide2 from "../assets/Dukamo Hawassa/slide2.jpg";
import slide3 from "../assets/Dukamo Hawassa/slide3.jpg";
import slide4 from "../assets/Dukamo Hawassa/slide4.jpg";
import slide5 from "../assets/Dukamo Hawassa/slide5.jpg";
import slide6 from "../assets/Dukamo Hawassa/slide6.jpg";
import slide7 from "../assets/Dukamo Hawassa/slide7.jpg";

// Specialty Images
import hawassaJebena from "../assets/Dukamo Hawassa/jebena.jpg";
import hawassaRanger from "../assets/Dukamo Hawassa/ranger.jpg";

const defaultTopSliderImages = [
  slide1,
  slide2,
  slide3,
  slide4,
  slide5,
  slide6,
  slide7,
];
const defaultPlaces = [
  "Lake Hawassa & Fish Market",
  "Amora Gedel Park",
  "Tabor Mountain",
  "Gudumale Park",
];

function DukamoHawassaPage() {
  const [currentTopSlide, setCurrentTopSlide] = useState(0);
  const [content, setContent] = useState({ textFields: {}, imageFields: {} });
  const [topSliderImages, setTopSliderImages] = useState(
    defaultTopSliderImages
  );
  const [placesToVisit, setPlacesToVisit] = useState(defaultPlaces);

  // 1. Fetch CMS Content
  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get("/api/pages/location-hawassa")
      .then((res) => {
        if (res.data) {
          setContent(res.data);

          // A. Build Dynamic Slider
          const dynamicSlider = [];
          if (res.data.imageFields) {
            for (let i = 0; i < 20; i++) {
              const key = `hawassaSlider_${i}`;
              if (res.data.imageFields[key]) {
                dynamicSlider.push(`${res.data.imageFields[key]}`);
              }
            }
          }
          if (dynamicSlider.length > 0) {
            setTopSliderImages(dynamicSlider);
          }

          // B. Parse Places List
          if (res.data.textFields && res.data.textFields.attractionsList) {
            const list = res.data.textFields.attractionsList
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s !== "");
            if (list.length > 0) setPlacesToVisit(list);
          }
        }
      })
      .catch((err) => console.log("Using default content"));
  }, []);

  const getText = (key, fallback) =>
    content.textFields && content.textFields[key]
      ? content.textFields[key]
      : fallback;
  const getImage = (key, fallback) =>
    content.imageFields && content.imageFields[key]
      ? `${content.imageFields[key]}`
      : fallback;

  // 2. Slider Logic
  useEffect(() => {
    const topSlideInterval = setInterval(() => {
      setCurrentTopSlide((prev) => (prev + 1) % topSliderImages.length);
    }, 5000);
    return () => clearInterval(topSlideInterval);
  }, [topSliderImages]);

  const nextSlide = () =>
    setCurrentTopSlide((prev) => (prev + 1) % topSliderImages.length);
  const prevSlide = () =>
    setCurrentTopSlide(
      (prev) => (prev - 1 + topSliderImages.length) % topSliderImages.length
    );
  const goToTopSlide = (index) => setCurrentTopSlide(index);

  return (
    <div className={styles.locationPage}>
      <Navbar />

      {/* 1. INTRO */}
      <section className={styles.locationIntro}>
        <div className={styles.introTextContent}>
          <h1>{getText("introTitle", "Dukamo Hawassa")}</h1>
          <p className={styles.visitText}>
            {getText("visitText", "Visit our Hawassa location")}
          </p>
          <p
            className={styles.locationDescription}
            style={{ whiteSpace: "pre-line" }}
          >
            {getText(
              "introText",
              "Hawassa, a vibrant city in southern Ethiopia, located about 275 km from Addis Ababa."
            )}
          </p>
        </div>
      </section>

      {/* 2. SLIDER */}
      <section className={`${styles.imageSliderSection} ${styles.topSlider}`}>
        <div className={styles.sliderContainer}>
          {topSliderImages.map((image, index) => (
            <div
              key={`${index}-${image}`}
              className={`${styles.slide} ${
                index === currentTopSlide ? styles.active : ""
              }`}
            >
              <img src={image} alt={`Dukamo Hawassa Slide ${index + 1}`} />
            </div>
          ))}
          <button
            className={`${styles.sliderArrow} ${styles.prev}`}
            onClick={prevSlide}
          >
            <FaChevronLeft />
          </button>
          <button
            className={`${styles.sliderArrow} ${styles.next}`}
            onClick={nextSlide}
          >
            <FaChevronRight />
          </button>
          <div className={styles.sliderDots}>
            {topSliderImages.map((_, index) => (
              <span
                key={index}
                className={`${styles.dot} ${
                  index === currentTopSlide ? styles.active : ""
                }`}
                onClick={() => goToTopSlide(index)}
              ></span>
            ))}
          </div>
        </div>
      </section>

      {/* 3. SPECIALTY */}
      <section className={styles.hawassaSpeciality}>
        <h2>{getText("specialityTitle", "Our Speciality")}</h2>
        <div className={styles.specialityItems}>
          {/* Item 1 */}
          <div className={styles.specialityItem}>
            <img src={getImage("spec1Image", hawassaJebena)} alt="Jebena" />
            <h3>{getText("spec1Title", "Jebena")}</h3>
            <p style={{ whiteSpace: "pre-line" }}>
              {getText(
                "spec1Text",
                "Our Jebena is so loved by our customers. People come 2-3 times a day to enjoy it."
              )}
            </p>
          </div>
          {/* Item 2 */}
          <div className={styles.specialityItem}>
            <img src={getImage("spec2Image", hawassaRanger)} alt="Ranger" />
            <h3>{getText("spec2Title", "Ranger")}</h3>
            <p style={{ whiteSpace: "pre-line" }}>
              {getText(
                "spec2Text",
                "A unique milk-based coffee drink known in the region. It’s a must-try if you want to taste something special from the city’s coffee culture."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* 4. ATTRACTIONS */}
      <section className={styles.hawassaAttractions}>
        <div className={styles.attractionsImage}>
          <img
            src={getImage("attractionsImage", hawassaHeroImage)}
            alt="Hawassa scenery"
          />
        </div>
        <div className={styles.attractionsContent}>
          <h2>{getText("attractionsTitle", "Places to Visit in Hawassa")}</h2>
          <ul className={styles.placesList}>
            {placesToVisit.map((place, index) => (
              <li key={index}>{place}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5. CONTACT */}
      <section className={styles.contactHoursBottomSection}>
        <div className={styles.contactInfo}>
          <h3>Contact Us</h3>
          <p>Phone: {getText("contactPhone", "(+251)919046565")}</p>
          <p>Email: {getText("contactEmail", "info@bunchocoffee.com")}</p>
        </div>
        <div className={styles.hoursInfo}>
          <h3>Hours of operation</h3>
          <p>
            {getText(
              "hours",
              "Monday to Sunday 8:00 AM – 8:00 PM (2:00Lt -2:00Lt)"
            )}
          </p>
          <p>
            Location:{" "}
            {getText("address", "Daye Bensa Building, Sumuda Gebriel, Hawassa")}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default DukamoHawassaPage;
